import Vue from "vue";
import * as api from "@lab/api/hospitals.api";
import { putAutoEmails, createAutoEmails } from "@lab/api/emails.api";
// import * as userApi from "@lab/api/users.api";
// import asyncForEach from "async-await-foreach";

import { radiusSearch } from "@lab/api/suburbs.api";
import { Hospital, HospitalUserInvite } from "@lab/types/hospitals";
import { Catchment } from "@lab/types/locations";
import { SetCatchmentType } from "@lab/types/searches";
import { Group } from "@/shared/types/groups";
import { sortBy } from "lodash";

const state = {
	byID: [],
	isFetching: false,
	errorMessage: null,
	lastFetched: null // Timestamp
};

const getters = {
	get: (state, getters): Hospital[] =>
		state.byID ? Object.values(state.byID) : [],
	getHospital:
		(state: any) =>
		(id: number): Hospital => {
			return state.byID[id];
		},
	getHealthService:
		(state, getters, rootState, rootGetters) => (id: number) => {
			return rootGetters["healthservices/getHealthService"](id);
		},
	hospitals: (state, getters) => sortBy(getters.get, ["name"]),
	IsFetching: (state): boolean => state.isFetching,
	error: (state): string => state.errorMessage
};

const actions = {
	async fetchHospitals({ commit, dispatch }) {
		try {
			commit("LOAD_START");
			const hospitals = await api.getHospitals();

			dispatch("healthservices/fetchHealthServices", null, {
				root: true
			});

			commit("LOAD_LIST_SUCCESS", hospitals);
		} catch (error) {
			// handle the error here
		}
	},
	async fetchHospital({ commit, dispatch }, id: number) {
		// Do we already have the user?
		/**
		 * @TODO: need to think this logic through a little more
		 * before we implement it
		 */
		// if (getters.getHospital(id)) return;
		commit(`LOAD_START`);
		// 1. get the base hospital object
		const hospital = await api.getHospital(id);
		// 2a. return the hospital groups
		const groups = await api.getGroups(id);
		// 2b. added the groups to the hospital object
		hospital.groups = groups;
		// 3a. return the hospital users
		const users = await api.getUsers(id);
		// 3b. add the users to the hospital object
		hospital.users = users;

		if (hospital.healthserviceid) {
			await dispatch(
				"healthservices/fetchHealthService",
				hospital.healthserviceid,
				{
					root: true
				}
			);
			delete hospital.healthservice;
		}

		// 4 Save the hospital to the VUEX state.
		commit(`LOAD_ITEM_SUCCESS`, hospital);
	},
	async updateHospital({ commit }, hospital: Hospital) {
		const response = await api.putHospital(hospital);
		if (hospital.catchments) {
			api.postCatchments(hospital.id, hospital.catchments);
		}

		if (hospital.digest) {
			const { digest } = hospital;
			if (!digest.id) {
				await createAutoEmails(digest);
			} else {
				await putAutoEmails(digest);
			}
		}

		commit(`UPDATE_ITEM_SUCCESS`, response);
	},
	async deleteHospital({ commit }, hospital: Hospital) {
		commit(`LOAD_START`);

		await api.removeHospital(hospital.id);

		commit("DELETE_ITEM_SUCCESS", hospital);
	},
	async deleteHospitalGroup({ commit }, group: Group) {
		const { hospitalid, id } = group;
		await api.deleteGroup(hospitalid, id);
		commit("REMOVE_GROUP", group);
	},
	addCatchment({ commit }, catchment: Catchment) {
		commit(`ADD_CATCHMENT`, catchment);
	},
	removeCatchment({ commit }, catchment: Catchment) {
		commit("REMOVE_CATCHMENT", catchment);
	},
	clearCatchments({ commit }, id: number) {
		commit("CLEAR_CATCHMENT", id);
	},
	async setCatchment({ commit }, { id, search }: SetCatchmentType) {
		const suburbs = await radiusSearch(search);
		commit("SET_CATCHMENTS", { id, suburbs });
	},
	async usersInvite({ commit }, invitation: HospitalUserInvite) {
		await api.inviteUsers(invitation);
	}
};

const mutations = {
	ADD_CATCHMENT(state: any, { id, suburb }: Catchment) {
		if (!state.byID[id].catchments) {
			state.byID[id].catchments = [];
		}
		state.byID[id].catchments.push(suburb);
	},
	REMOVE_GROUP(state, group: Group) {
		const { id: groupid, hospitalid } = group;
		const removed = state.byID[hospitalid].groups.filter(
			(remove) => remove.id !== groupid
		);
		state.byID[hospitalid].groups = removed;
	},
	REMOVE_CATCHMENT(state, { id, suburb }: Catchment) {
		const removed = state.byID[id].catchments.filter(
			(remove) => remove.id !== suburb.id
		);
		state.byID[id].catchments = removed;
	},
	SET_CATCHMENTS(state, { id, suburbs }) {
		state.byID[id].catchments = suburbs;
	},
	CLEAR_CATCHMENT(state, id: number) {
		state.byID[id].catchments = [];
	},
	LOAD_LIST_SUCCESS(state: any, hospitals: Hospital[]) {
		const byID = hospitals.reduce((acc, val) => {
			acc[val.id] = val;
			return acc;
		}, {});
		Vue.set(state, "byID", byID);
		state.lastFetched = Date.now();
		state.isFetching = false;
	},
	LOAD_ITEM_SUCCESS(state: any, hospital: Hospital) {
		if (!hospital) return;
		Vue.set(state.byID, hospital.id.toString(), hospital);
		state.isFetching = false;
	},
	UPDATE_ITEM_SUCCESS(state: any, hospital: Hospital) {
		Vue.set(state.byID, hospital.id.toString(), hospital);
	},
	DELETE_ITEM_SUCCESS(state: any, hospital: Hospital) {
		const { id } = hospital;
		Vue.delete(state.byID, id);
		state.isFetching = false;
	},

	LOAD_START(state: any) {
		state.errorMessage = null;
		state.isFetching = true;
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
